import React, { FC, useState, useEffect, useMemo } from 'react';
import { BmisSummaryCard } from '@web/bmis/components/bmis-summary-card';
import { Col, Row } from 'react-bootstrap';
import { DetailView, DetailViewBody, DetailViewContent } from '@web/components/detail-view';
import { QmrReports } from '../components/qmr-reports';
import { QuickReferences } from '../components/quick-references/quick-references';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import { DmsSummaryCard } from '../components/dms/components/dms-summary-card';
import { CampaignSummaryCard } from '../components/campaigns/components/campaigns-summary-card';
import { SecurityCodeBanner, useSecurityCodeNotification } from '@web/components/security-code-banner';
import { AuthorizationCodeModal } from '@web/components/authorization-code-modal';
import { useVinSessionState } from '../context/vin-sessions.hooks';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    skeletonContainer: {
        padding: 24,
    },
    skeletonRow: {
        display: 'flex',
        gap: 16,
        marginBottom: 24,
    },
    skeletonCard: {
        flex: 1,
        borderRadius: 8,
        border: '1px solid #E3E3E3',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    skeletonSection: {
        marginBottom: 24,
    },
});

const DashboardSkeleton: FC = () => {
    const classes = useStyles();
    return (
        <div className={classes.skeletonContainer}>
            <div className={classes.skeletonRow}>
                {[1, 2, 3].map((i) => (
                    <div key={i} className={classes.skeletonCard}>
                        <SkeletonLoader type="text" width="60%" height={16} />
                        <SkeletonLoader type="text" width="80%" height={12} />
                        <SkeletonLoader type="text" width="40%" height={12} />
                    </div>
                ))}
            </div>
            <div className={classes.skeletonSection}>
                <SkeletonLoader type="text" width={180} height={20} />
            </div>
            <div className={classes.skeletonRow}>
                {[1, 2, 3].map((i) => (
                    <div key={i} className={classes.skeletonCard}>
                        <SkeletonLoader type="text" width="50%" height={14} />
                        <SkeletonLoader type="text" width="100%" height={80} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export const VinSessionDashboard: FC = () => {
    const [authCodeModalOpen, setAuthCodeModalOpen] = useState(false);
    const { isOtpwFlowFeatureEnabled, isQuickReferencesFeatureEnabled } = useFeatureFlags();

    const securityCodeData = useSecurityCodeNotification();
    const { selectedVin, isVinLoading } = useVinSessionState();

    const currentVinSecurityCode = useMemo(() => {
        const vinString = selectedVin?.vinRecord?.vin;
        if (!vinString || !securityCodeData.activeVins.length) return null;
        return securityCodeData.activeVins.find((v) => v.vin === vinString) || null;
    }, [selectedVin, securityCodeData.activeVins]);

    const hasSecurityCodeForCurrentVin = !!currentVinSecurityCode;

    useEffect(() => {
        const handleOpenAuthModal = () => {
            if (hasSecurityCodeForCurrentVin) {
                setAuthCodeModalOpen(true);
            }
        };
        window.addEventListener('openAuthorizationCodeModal', handleOpenAuthModal);
        return () => {
            window.removeEventListener('openAuthorizationCodeModal', handleOpenAuthModal);
        };
    }, [hasSecurityCodeForCurrentVin]);

    if (isVinLoading || !selectedVin) {
        return <DashboardSkeleton />;
    }

    return (
        <DetailView>
            <DetailViewContent topOffset="breadcrumb">
                <DetailViewBody>
                    {isOtpwFlowFeatureEnabled && hasSecurityCodeForCurrentVin && currentVinSecurityCode && (
                        <Row className="mb-4">
                            <Col>
                                <SecurityCodeBanner
                                    expiresAt={currentVinSecurityCode.expiresAt}
                                    onButtonClick={() => setAuthCodeModalOpen(true)}
                                    buttonText="showAuthorizationCode"
                                />
                            </Col>
                        </Row>
                    )}

                    {isOtpwFlowFeatureEnabled && hasSecurityCodeForCurrentVin && currentVinSecurityCode && (
                        <AuthorizationCodeModal
                            show={authCodeModalOpen}
                            onHide={() => setAuthCodeModalOpen(false)}
                            vin={currentVinSecurityCode.vin}
                            expiresAt={currentVinSecurityCode.expiresAt}
                            otpwAssignmentId={currentVinSecurityCode.otpwAssignmentId}
                        />
                    )}

                    <Row>
                        <Col xs={12} md={6} lg={4} className="mb-2">
                            <DmsSummaryCard />
                        </Col>

                        <Col xs={12} md={6} lg={4} className="mb-2">
                            <BmisSummaryCard />
                        </Col>

                        <Col xs={12} md={6} lg={4} className="mb-2">
                            <CampaignSummaryCard />
                        </Col>
                    </Row>

                    {isQuickReferencesFeatureEnabled && (
                        <Row>
                            <Col className="mb-4">
                                <QuickReferences />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col className="mb-4">
                            <QmrReports />
                        </Col>
                    </Row>
                </DetailViewBody>
            </DetailViewContent>
        </DetailView>
    );
};
